import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sanitizeStrict, sanitizeRich } from '../../lib/sanitize';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Bot, AlertTriangle, PhoneCall, Loader2, Play, Clock, Copy, Sparkles, ChevronRight, X, Send, Target, DollarSign, FileText as FileIcon, Settings as SettingsIcon, Mail, Save, Search, Edit3, Trash2, ShieldAlert, ExternalLink, Users, ArrowRight, ArrowLeft, MessageCircle, Mic, Square } from 'lucide-react';
import AutoPilot from './AutoPilot';

const CATEGORIA_COLORS = {
    cantiere: { bg: 'bg-[hsl(30_90%_92%)]', text: 'text-[hsl(25_80%_25%)]', labelKey: 'pages.ag_cat_jobsite' },
    amministrazione: { bg: 'bg-[hsl(280_70%_92%)]', text: 'text-[hsl(280_60%_25%)]', labelKey: 'pages.ag_cat_admin' },
    vendita: { bg: 'bg-[hsl(145_45%_88%)]', text: 'text-[hsl(145_55%_22%)]', labelKey: 'pages.ag_cat_sales' },
    centrale: { bg: 'bg-[hsl(220_13%_10%)]', text: 'text-[hsl(45_100%_55%)]', labelKey: 'pages.ag_cat_central' },
};

const AGENT_ICONS = {
    crm_clienti: Users,
    solleciti: AlertTriangle,
    followup_preventivi: PhoneCall,
    lead_finder: Target,
    preventivo_auto: DollarSign,
    fattura_auto: FileIcon,
    conversazioni_ai: MessageCircle,
};

export default function AgentiAI() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'catalogo';

    // viewMode: 'agents' (tabs catalogo/dashboard/storico/settings) | 'crm' (full-screen CRM)
    const [viewMode, setViewMode] = useState(initialTab === 'crm' ? 'crm' : 'agents');
    const [tab, setTab] = useState(initialTab === 'crm' ? 'catalogo' : initialTab);

    // Sync URL with current view
    useEffect(() => {
        const desired = viewMode === 'crm' ? 'crm' : tab;
        const current = searchParams.get('tab');
        if (current !== desired) {
            const sp = new URLSearchParams(searchParams);
            sp.set('tab', desired);
            setSearchParams(sp, { replace: true });
        }
    }, [tab, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCRM = () => setViewMode('crm');
    const backToAgents = () => setViewMode('agents');

    const [catalogo, setCatalogo] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [runs, setRuns] = useState([]);
    const [running, setRunning] = useState(null); // agente_id in esecuzione
    const [result, setResult] = useState(null);
    const [openLeadFinder, setOpenLeadFinder] = useState(false);
    const [openPreventivoAuto, setOpenPreventivoAuto] = useState(false);
    const resultRef = React.useRef(null);

    // Auto-scroll to result panel whenever a new agent run completes so the
    // user immediately sees the agent's output (instead of thinking nothing happened).
    useEffect(() => {
        if (result && resultRef.current) {
            // wait one frame so the DOM is painted, then scroll smoothly
            requestAnimationFrame(() => {
                try {
                    resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (_) { /* noop */ }
            });
        }
    }, [result]);

    const load = useCallback(async () => {
        try {
            const [cat, dash, runsRes] = await Promise.all([
                api.get('/agenti/catalogo'),
                api.get('/agenti/dashboard'),
                api.get('/agenti/runs'),
            ]);
            setCatalogo(cat.data.agenti);
            setDashboard(dash.data);
            setRuns(runsRes.data);
        } catch (e) {
            toast.error(t('pages.ag_toast_loaded_err'));
        }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const runAgent = async (agente) => {
        // Central CRM agent → enter CRM view (no run)
        if (agente.id === 'crm_clienti' || agente.action === 'open_crm') { openCRM(); return; }
        // Conversazioni AI → navigate to dedicated page
        if (agente.id === 'conversazioni_ai' || agente.action === 'open_conversations') { navigate('/app/lead-conversations?from=agenti'); return; }
        // Agents requiring input -> open dialog
        if (agente.id === 'lead_finder') { setOpenLeadFinder(true); return; }
        if (agente.id === 'preventivo_auto') { setOpenPreventivoAuto(true); return; }

        // Auto-run agents (no input required)
        setRunning(agente.id);
        setResult(null);
        try {
            const endpointMap = {
                'solleciti': '/agenti/solleciti/generate',
                'followup_preventivi': '/agenti/followup-preventivi/generate',
                'fattura_auto': '/agenti/fattura-auto/scan',
            };
            const res = await api.post(endpointMap[agente.id], {});
            setResult({ agente, data: res.data });
            const count = res.data.solleciti_generati ?? res.data.followup_generati ?? res.data.bozze_generate ?? 0;
            toast.success(t('pages.ag_toast_result_gen', { count }));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.ag_toast_run_err'));
        } finally {
            setRunning(null);
        }
    };

    return (
        <div data-testid="agenti-ai-page">
            {viewMode === 'crm' ? (
                /* ============================================================
                 * CRM full-screen view (entered via "Apri CRM" button on the
                 * CRM Clienti agent card). Provides a back button to return
                 * to the agents catalog.
                 * ============================================================ */
                <div data-testid="agenti-crm-view">
                    <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                        <Button
                            onClick={backToAgents}
                            variant="ghost"
                            className="gap-2 -ml-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            data-testid="agenti-crm-back"
                        >
                            <ArrowLeft className="w-4 h-4" /> {t('pages.ag_crm_back') || 'Torna agli Agenti'}
                        </Button>
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(145_55%_45%)] animate-pulse" />
                            {t('pages.ag_crm_active') || 'CRM · sincronizzazione attiva con tutti gli agenti'}
                        </div>
                    </div>
                    <div className="mb-4">
                        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
                            <Users className="w-8 h-8 text-[hsl(var(--accent))]" /> {t('pages.ag_tab_crm') || 'CRM · Gestione Clienti'}
                        </h1>
                    </div>
                    <AutoPilot embedded />
                </div>
            ) : (
                /* ============================================================
                 * Agents catalog view (4 tabs, no CRM tab — CRM accessed only
                 * via the central card)
                 * ============================================================ */
                <>
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))]">PLUS+</div>
                            <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
                                <Sparkles className="w-10 h-10 text-[hsl(var(--accent))]" /> {t('pages.ag_title')}
                            </h1>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.ag_subtitle')}</p>
                        </div>
                    </div>

                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList>
                            <TabsTrigger value="catalogo" data-testid="agenti-tab-catalogo">{t('pages.ag_tab_catalog')} ({catalogo.length})</TabsTrigger>
                            <TabsTrigger value="dashboard" data-testid="agenti-tab-dashboard">{t('pages.ag_tab_stats')}</TabsTrigger>
                            <TabsTrigger value="storico" data-testid="agenti-tab-storico">{t('pages.ag_tab_history')} ({runs.length})</TabsTrigger>
                            <TabsTrigger value="settings" data-testid="agenti-tab-settings">{t('pages.ag_tab_settings')}</TabsTrigger>
                        </TabsList>

                        {/* Catalogo agenti — 3 fasi con separatori */}
                        <TabsContent value="catalogo" className="mt-4">
                    {(() => {
                        const sorted = [...catalogo].sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));
                        const centrale = sorted.filter((a) => (a.phase || (a.is_central ? 'centrale' : 'fase2')) === 'centrale');
                        const fase1 = sorted.filter((a) => a.phase === 'fase1');
                        const fase2 = sorted.filter((a) => a.phase === 'fase2');

                        const renderCard = (ag) => {
                            const Icon = AGENT_ICONS[ag.id] || Bot;
                            const cat = CATEGORIA_COLORS[ag.categoria] || CATEGORIA_COLORS.cantiere;
                            const isRunning = running === ag.id;
                            const isCentral = !!ag.is_central;
                            const agName = t(`agents.${ag.id}.name`, { defaultValue: ag.nome });
                            const agDesc = t(`agents.${ag.id}.description`, { defaultValue: ag.descrizione });
                            const tBullets = t(`agents.${ag.id}.bullets`, { returnObjects: true, defaultValue: ag.cosa_fa });
                            const agBullets = Array.isArray(tBullets) ? tBullets : ag.cosa_fa;
                            return (
                                <Card
                                    key={ag.id}
                                    className={`p-6 flex flex-col transition-all ${
                                        isCentral
                                            ? 'border-2 border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--accent))]/8 to-transparent ring-1 ring-[hsl(var(--accent))]/20 hover:shadow-lg'
                                            : 'hover:border-[hsl(var(--accent))]'
                                    }`}
                                    data-testid={`agente-card-${ag.id}`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-2xl ${
                                            isCentral
                                                ? 'bg-[hsl(220_13%_10%)] border-2 border-[hsl(var(--accent))]'
                                                : 'bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30'
                                        }`}>
                                            {isCentral ? <span>{ag.emoji || '👥'}</span> : <Icon className="w-6 h-6 text-[hsl(var(--accent))]" />}
                                        </div>
                                        <Badge className={`${cat.bg} ${cat.text} hover:${cat.bg} font-bold tracking-wider`}>{t(cat.labelKey)}</Badge>
                                    </div>
                                    <h3 className={`font-display tracking-wide mb-2 ${isCentral ? 'text-3xl' : 'text-2xl'}`}>{agName.toUpperCase()}</h3>
                                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 flex-1">{agDesc}</p>
                                    <div className="space-y-1.5 mb-5">
                                        {agBullets.slice(0, 4).map((f, i) => (
                                            <div key={i} className="flex items-start gap-2 text-xs">
                                                <span className="text-[hsl(var(--accent))] mt-0.5">▸</span>
                                                <span>{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <Button
                                        onClick={() => runAgent(ag)}
                                        disabled={isRunning}
                                        className={`w-full font-bold h-11 gap-2 ${
                                            isCentral
                                                ? 'bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-[hsl(45_100%_55%)]'
                                                : 'bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)]'
                                        }`}
                                        data-testid={`agente-run-${ag.id}`}
                                    >
                                        {isCentral ? (
                                            <><Users className="w-4 h-4" /> {t('pages.ag_open_crm') || 'Apri CRM'} <ArrowRight className="w-4 h-4" /></>
                                        ) : ag.id === 'conversazioni_ai' ? (
                                            <><MessageCircle className="w-4 h-4" /> {t('pages.ag_open_conversations') || 'Apri Conversazioni'} <ArrowRight className="w-4 h-4" /></>
                                        ) : isRunning ? (
                                            <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.ag_running')}</>
                                        ) : (
                                            <><Play className="w-4 h-4" /> {t('pages.ag_run', { defaultValue: 'Esegui ora' })}</>
                                        )}
                                    </Button>
                                </Card>
                            );
                        };

                        const PhaseSeparator = ({ label }) => (
                            <div className="flex items-center gap-4 my-6" role="separator" aria-label={label}>
                                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                                <span className="text-xs md:text-sm font-bold tracking-[0.18em] uppercase text-[hsl(220_13%_25%)] whitespace-nowrap px-2">
                                    {label}
                                </span>
                                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                            </div>
                        );

                        return (
                            <>
                                {centrale.length > 0 && (
                                    <div className="grid grid-cols-1 gap-5" data-testid="agenti-row-centrale">
                                        {centrale.map(renderCard)}
                                    </div>
                                )}
                                {fase1.length > 0 && (
                                    <PhaseSeparator label={t('pages.ag_phase_1') || 'Fase 1 · Acquisizione clienti'} />
                                )}
                                {fase1.length > 0 && (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agenti-row-fase1">
                                        {fase1.map(renderCard)}
                                    </div>
                                )}
                                {fase2.length > 0 && (
                                    <PhaseSeparator label={t('pages.ag_phase_2') || 'Fase 2 · Chiusura e incasso'} />
                                )}
                                {fase2.length > 0 && (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agenti-row-fase2">
                                        {fase2.map(renderCard)}
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    {/* Last result preview */}
                    {result && (
                        <Card ref={resultRef} className="p-6 mt-6 border-[hsl(var(--accent))] border-2" data-testid="agenti-last-result">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-display text-2xl">{t('pages.ag_result_title')}: {t(`agents.${result.agente.id}.name`, { defaultValue: result.agente.nome }).toUpperCase()}</h3>
                                <Button variant="ghost" size="sm" onClick={() => setResult(null)}><X className="w-4 h-4" /></Button>
                            </div>
                            <ResultDisplay agente={result.agente} data={result.data} />
                        </Card>
                    )}
                </TabsContent>

                {/* Dashboard */}
                <TabsContent value="dashboard" className="mt-4">
                    {dashboard && (
                        <>
                            <Card className="p-6 mb-4">
                                <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{t('pages.ag_kpi_total')}</div>
                                <div className="font-display text-5xl tabular-nums" data-testid="agenti-kpi-total">{dashboard.total_runs}</div>
                            </Card>
                            <div className="grid md:grid-cols-3 gap-4">
                                {catalogo.map((ag) => {
                                    const stats = dashboard.by_agent[ag.id] || { count: 0, last_run: null };
                                    return (
                                        <Card key={ag.id} className="p-5">
                                            <div className="flex items-center gap-2 mb-2 text-sm">
                                                <span className="text-2xl">{ag.emoji}</span>
                                                <span className="font-semibold">{t(`agents.${ag.id}.name`, { defaultValue: ag.nome })}</span>
                                            </div>
                                            <div className="font-display text-3xl tabular-nums">{stats.count}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_kpi_runs')}</div>
                                            {stats.last_run && (
                                                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> {t('pages.ag_kpi_last')} {new Date(stats.last_run).toLocaleDateString()}
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </TabsContent>

                {/* Storico */}
                <TabsContent value="storico" className="mt-4">
                    <Card className="overflow-x-auto">
                        {runs.length === 0 ? (
                            <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                                <Bot className="w-12 h-12 mx-auto mb-3" />
                                <p>{t('pages.ag_empty_history')}</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-[hsl(var(--muted))]">
                                    <tr className="text-left">
                                        <th className="p-3">{t('pages.ag_col_agent')}</th>
                                        <th className="p-3">{t('pages.ag_col_date')}</th>
                                        <th className="p-3">{t('pages.ag_col_duration')}</th>
                                        <th className="p-3">{t('pages.ag_col_status')}</th>
                                        <th className="p-3">{t('pages.ag_col_input')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map((r) => {
                                        const ag = catalogo.find(a => a.id === r.agente_id) || { nome: r.agente_id, emoji: '🤖' };
                                        return (
                                            <tr key={r.id} className="border-t" data-testid={`run-${r.id}`}>
                                                <td className="p-3"><span className="mr-1">{ag.emoji}</span> {t(`agents.${ag.id}.name`, { defaultValue: ag.nome })}</td>
                                                <td className="p-3 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                                                <td className="p-3 text-xs tabular-nums">{r.durata_secondi}s</td>
                                                <td className="p-3">
                                                    <Badge className={r.stato === 'completato' ? 'bg-[hsl(145_55%_84%)] text-[hsl(145_60%_18%)]' : 'bg-[hsl(0_70%_88%)] text-[hsl(0_70%_25%)]'}>
                                                        {r.stato}
                                                    </Badge>
                                                </td>
                                                <td className="p-3 text-xs text-[hsl(var(--muted-foreground))]">{r.input_summary}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </TabsContent>
                {/* Settings */}
                <TabsContent value="settings" className="mt-4">
                    <SettingsPanel />
                </TabsContent>
            </Tabs>
                </>
            )}

            <LeadFinderDialog open={openLeadFinder} onOpenChange={setOpenLeadFinder} onCompleted={(d) => { setResult({ agente: catalogo.find(a => a.id === 'lead_finder'), data: d }); setOpenLeadFinder(false); setTab('catalogo'); setViewMode('agents'); load(); }} />
            <PreventivoAutoDialog open={openPreventivoAuto} onOpenChange={setOpenPreventivoAuto} onCompleted={(d) => { setResult({ agente: catalogo.find(a => a.id === 'preventivo_auto'), data: d }); setOpenPreventivoAuto(false); setTab('catalogo'); setViewMode('agents'); load(); }} />
        </div>
    );
}


// =========================================================
// Result Display (per ogni tipo di agente)
// =========================================================
function ResultDisplay({ agente, data }) {
    const { t } = useTranslation();
    const copy = (text) => { navigator.clipboard.writeText(text); toast.success(t('common.copied')); };

    if (agente.id === 'solleciti') {
        const items = data.solleciti || [];
        if (items.length === 0) {
            return <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.ag_solleciti_empty')}</p>;
        }
        return (
            <div className="space-y-3">
                <div className="text-sm" dangerouslySetInnerHTML={sanitizeStrict(t('pages.ag_solleciti_generated', { n: items.length }))} />
                {items.map((s, i) => (
                    <Card key={i} className="p-4">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div>
                                <div className="font-semibold">{s.cliente}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_solleciti_invoice', { num: s.fattura_numero, amt: Number(s.importo).toFixed(2), days: s.giorni_ritardo })}</div>
                            </div>
                            <Badge className={
                                s.livello === 4 ? 'bg-[hsl(0_70%_88%)] text-[hsl(0_70%_25%)]' :
                                s.livello === 3 ? 'bg-[hsl(15_85%_88%)] text-[hsl(15_75%_25%)]' :
                                s.livello === 2 ? 'bg-[hsl(45_90%_85%)] text-[hsl(35_80%_25%)]' :
                                'bg-[hsl(145_45%_88%)] text-[hsl(145_55%_22%)]'
                            }>Livello {s.livello}</Badge>
                        </div>
                        <div className="text-xs font-semibold mb-1 text-[hsl(var(--muted-foreground))]">{t('pages.ag_email_subject_label')}</div>
                        <div className="text-sm mb-2">{s.oggetto}</div>
                        <div className="text-xs font-semibold mb-1 text-[hsl(var(--muted-foreground))]">{t('pages.ag_email_body_label')}</div>
                        <div className="text-sm border rounded p-2 bg-white max-h-32 overflow-y-auto" dangerouslySetInnerHTML={sanitizeRich(s.corpo_html)} />
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => copy(s.corpo_html.replace(/<[^>]+>/g, ''))} className="gap-1"><Copy className="w-3 h-3" /> {t('common.copy')}</Button>
                            <SendEmailButton to={s.email_cliente} subject={s.oggetto} html={s.corpo_html} tipo="sollecito" refId={s.fattura_id} />
                        </div>
                    </Card>
                ))}
            </div>
        );
    }

    if (agente.id === 'followup_preventivi') {
        const items = data.followup || [];
        if (items.length === 0) {
            return <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.ag_followup_empty')}</p>;
        }
        return (
            <div className="space-y-3">
                <div className="text-sm" dangerouslySetInnerHTML={sanitizeStrict(t('pages.ag_followup_generated', { n: items.length }))} />
                {items.map((s, i) => (
                    <Card key={i} className="p-4">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div>
                                <div className="font-semibold">{s.cliente}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_followup_meta', { amt: Number(s.importo).toFixed(2), days: s.giorni_attesa })}{s.email_cliente ? ` · ${s.email_cliente}` : ''}</div>
                            </div>
                            <Badge className={
                                s.tono === 'urgente' ? 'bg-[hsl(15_85%_88%)] text-[hsl(15_75%_25%)]' :
                                s.tono === 'interessato' ? 'bg-[hsl(45_90%_85%)] text-[hsl(35_80%_25%)]' :
                                'bg-[hsl(210_90%_88%)] text-[hsl(210_80%_25%)]'
                            }>{s.tono}</Badge>
                        </div>
                        <div className="text-xs font-semibold mb-1 text-[hsl(var(--muted-foreground))]">{t('pages.ag_email_subject_label')}</div>
                        <div className="text-sm mb-2">{s.oggetto}</div>
                        <div className="text-sm border rounded p-2 bg-white max-h-32 overflow-y-auto" dangerouslySetInnerHTML={sanitizeRich(s.corpo_html)} />
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => copy(s.corpo_html.replace(/<[^>]+>/g, ''))} className="gap-1"><Copy className="w-3 h-3" /> {t('common.copy')}</Button>
                            <SendEmailButton to={s.email_cliente} subject={s.oggetto} html={s.corpo_html} tipo="followup" refId={s.preventivo_id} />
                        </div>
                    </Card>
                ))}
            </div>
        );
    }

    if (agente.id === 'lead_finder') {
        return (
            <div className="space-y-4 text-sm">
                <div className="p-3 rounded-lg bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30">
                    <strong>Analisi:</strong> {data.settore_analizzato} · {data.zona_analizzata}
                </div>
                {data.ricerche_google?.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold tracking-widest text-[hsl(var(--muted-foreground))] mb-2">🔍 RICERCHE GOOGLE ({data.ricerche_google.length})</div>
                        <div className="space-y-1">
                            {data.ricerche_google.map((q, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 rounded bg-[hsl(var(--muted))]/40">
                                    <code className="text-xs flex-1 break-all">{q}</code>
                                    <Button size="sm" variant="ghost" onClick={() => copy(q)}><Copy className="w-3 h-3" /></Button>
                                    <a href={`https://www.google.com/search?q=${encodeURIComponent(q)}`} target="_blank" rel="noopener noreferrer">
                                        <Button size="sm" variant="ghost">↗</Button>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {data.directory_italiani?.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold tracking-widest text-[hsl(var(--muted-foreground))] mb-2">📚 DIRECTORY ITALIANI</div>
                        <div className="grid sm:grid-cols-2 gap-2">
                            {data.directory_italiani.map((d, i) => (
                                <Card key={i} className="p-3">
                                    <div className="font-semibold text-sm">{d.nome}</div>
                                    {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(var(--accent))] underline truncate block">{d.url}</a>}
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{d.perche}</div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
                {data.hashtag_instagram?.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold tracking-widest text-[hsl(var(--muted-foreground))] mb-2">📷 HASHTAG INSTAGRAM</div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.hashtag_instagram.map((h, i) => (
                                <Badge key={i} className="bg-[hsl(280_70%_92%)] text-[hsl(280_60%_25%)] hover:bg-[hsl(280_70%_92%)] cursor-pointer" onClick={() => copy(h)}>{h}</Badge>
                            ))}
                        </div>
                    </div>
                )}
                {data.hashtag_linkedin?.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold tracking-widest text-[hsl(var(--muted-foreground))] mb-2">💼 HASHTAG LINKEDIN</div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.hashtag_linkedin.map((h, i) => (
                                <Badge key={i} className="bg-[hsl(210_90%_92%)] text-[hsl(210_80%_25%)] hover:bg-[hsl(210_90%_92%)] cursor-pointer" onClick={() => copy(h)}>{h}</Badge>
                            ))}
                        </div>
                    </div>
                )}
                {data.strategie_outreach?.length > 0 && (
                    <div className="p-3 rounded-lg bg-[hsl(145_45%_92%)]">
                        <div className="text-xs font-semibold tracking-widest text-[hsl(145_55%_22%)] mb-2">🎯 STRATEGIE OUTREACH</div>
                        <ul className="text-sm space-y-1 list-disc list-inside">
                            {data.strategie_outreach.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </div>
                )}
                {data.next_steps?.length > 0 && (
                    <div className="p-3 rounded-lg bg-[hsl(var(--accent))]/10 border-l-4 border-l-[hsl(var(--accent))]">
                        <div className="text-xs font-semibold tracking-widest mb-2">✅ DA FARE OGGI</div>
                        <ul className="text-sm space-y-1 list-disc list-inside">
                            {data.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    if (agente.id === 'preventivo_auto') {
        return (
            <div className="space-y-3 text-sm">
                {data.preventivo_id_salvato && (
                    <div className="p-3 rounded-lg bg-[hsl(145_45%_92%)] border border-[hsl(145_45%_70%)]">
                        ✅ {t('pages.ag_pa_saved')} <code className="text-xs">{data.preventivo_id_salvato}</code>
                    </div>
                )}
                <Card className="p-4">
                    <h4 className="font-display text-xl mb-1">{data.oggetto}</h4>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{data.descrizione}</p>
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] border-b">
                                <th className="p-2">Tipo</th>
                                <th className="p-2">Descrizione</th>
                                <th className="p-2 text-right">Qtà</th>
                                <th className="p-2 text-right">Prezzo</th>
                                <th className="p-2 text-right">Totale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.voci || []).map((v, i) => (
                                <tr key={i} className="border-b">
                                    <td className="p-2 text-xs"><Badge className="bg-[hsl(var(--muted))]">{v.tipo}</Badge></td>
                                    <td className="p-2">{v.descrizione}</td>
                                    <td className="p-2 text-right tabular-nums">{v.quantita} {v.unita}</td>
                                    <td className="p-2 text-right tabular-nums">€{Number(v.prezzo_unitario || 0).toFixed(2)}</td>
                                    <td className="p-2 text-right tabular-nums font-semibold">€{Number(v.totale || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr><td colSpan="4" className="p-2 text-right">Subtotale</td><td className="p-2 text-right tabular-nums">€{Number(data.subtotale_imponibile || 0).toFixed(2)}</td></tr>
                            <tr><td colSpan="4" className="p-2 text-right">IVA {data.iva_pct || 22}%</td><td className="p-2 text-right tabular-nums">€{Number(data.iva_importo || 0).toFixed(2)}</td></tr>
                            <tr className="text-lg font-bold"><td colSpan="4" className="p-2 text-right font-display">TOTALE</td><td className="p-2 text-right tabular-nums text-[hsl(var(--accent))]">€{Number(data.totale || 0).toFixed(2)}</td></tr>
                        </tfoot>
                    </table>
                    {data.note && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3 italic">📝 {data.note}</p>}
                </Card>
            </div>
        );
    }

    if (agente.id === 'fattura_auto') {
        const items = data.bozze || [];
        if (items.length === 0) {
            return <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.ag_fa_empty')}</p>;
        }
        return (
            <div className="space-y-3">
                <div className="text-sm" dangerouslySetInnerHTML={sanitizeStrict(t('pages.ag_fa_found', { n: items.length }))} />
                {items.map((b, i) => <FatturaAutoCard key={i} bozza={b} />)}
            </div>
        );
    }

    return <pre className="text-xs overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
}


// =========================================================
// Fattura Auto - single card with save button
// =========================================================
function FatturaAutoCard({ bozza }) {
    const { t } = useTranslation();
    const [saved, setSaved] = useState(null);
    const [loading, setLoading] = useState(false);
    const save = async () => {
        setLoading(true);
        try {
            const res = await api.post('/agenti/fattura-auto/save', {
                cantiere_id: bozza.cantiere_id,
                milestone: bozza.milestone,
                importo: bozza.importo_fattura_sal,
                descrizione: bozza.descrizione_fattura || `SAL ${bozza.milestone}%`,
                cliente: bozza.cliente || 'Cliente',
                voci: bozza.voci_consigliate || [],
            });
            setSaved(res.data);
            toast.success(t('pages.ag_fa_saved', { n: res.data.numero }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };
    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                    <div className="font-semibold">{bozza.cantiere_nome}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_col_cliente')}: {bozza.cliente} · €{Number(bozza.importo_totale_contratto).toLocaleString('it-IT')}</div>
                </div>
                <Badge className="bg-[hsl(var(--accent))]/15 text-[hsl(220_13%_10%)]">SAL {bozza.milestone}%</Badge>
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{bozza.milestone_descritta || bozza.descrizione_fattura}</div>
            <div className="flex items-end justify-between flex-wrap gap-2">
                <div className="font-display text-3xl tabular-nums">€{Number(bozza.importo_fattura_sal).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                {saved ? (
                    <Badge className="bg-[hsl(145_55%_84%)] text-[hsl(145_60%_18%)]">✓ {saved.numero}</Badge>
                ) : (
                    <Button onClick={save} disabled={loading} size="sm" className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] gap-2" data-testid={`fattura-auto-save-${bozza.cantiere_id}`}>
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {t('pages.ag_fa_save')}
                    </Button>
                )}
            </div>
        </Card>
    );
}


// =========================================================
// Send Email button (uses /api/agenti/email/send)
// =========================================================
function SendEmailButton({ to, subject, html, tipo, refId }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [emailOverride, setEmailOverride] = useState('');
    const [showInput, setShowInput] = useState(false);

    const send = async (recipient) => {
        if (!recipient || !recipient.includes('@')) {
            setShowInput(true);
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/agenti/email/send', { to: recipient, subject, html, tipo, ref_id: refId });
            toast.success(res.data.sent_via_custom_domain ? t('pages.ag_email_sent_custom') : t('pages.ag_email_sent'));
            setSent(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };

    if (sent) return <Badge className="bg-[hsl(145_55%_84%)] text-[hsl(145_60%_18%)]">✓ {t('pages.ag_email_sent')}</Badge>;
    if (showInput) {
        return (
            <div className="flex gap-1 items-center">
                <Input
                    placeholder={t('pages.ag_email_recipient_ph')}
                    value={emailOverride}
                    onChange={(e) => setEmailOverride(e.target.value)}
                    className="h-8 text-xs w-56"
                />
                <Button size="sm" onClick={() => send(emailOverride)} disabled={loading} className="gap-1 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {t('common.send')}
                </Button>
            </div>
        );
    }
    return (
        <Button size="sm" onClick={() => send(to)} disabled={loading} className="gap-1 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {t('pages.ag_email_send')}
        </Button>
    );
}


// =========================================================
// Lead Finder Dialog
// =========================================================
function LeadFinderDialog({ open, onOpenChange, onCompleted }) {
    const { t } = useTranslation();
    const [step, setStep] = useState('input'); // input | review | sending | done
    const [settore, setSettore] = useState('');
    const [location, setLocation] = useState('');
    const [nMax, setNMax] = useState(15);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [batch, setBatch] = useState(null);
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState({});
    const [editing, setEditing] = useState(null); // lead_id being edited
    const [sendResult, setSendResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setStep('input'); setSettore(''); setLocation(''); setNMax(15);
        setBatch(null); setLeads([]); setSelected({}); setEditing(null); setSendResult(null);
        api.get('/agenti/lead-finder/status').then((r) => setStatus(r.data)).catch(() => {});
    }, [open]);

    const search = async () => {
        if (!settore.trim()) { toast.error(t('pages.lfp_no_sector')); return; }
        setLoading(true);
        try {
            const res = await api.post('/agenti/lead-finder/search', { settore, location, n_max: nMax });
            setBatch({ id: res.data.batch_id, settore: res.data.settore, location: res.data.location, n_leads: res.data.leads.length });
            setLeads(res.data.leads);
            const sel = {};
            res.data.leads.forEach((l) => { sel[l.id] = true; });
            setSelected(sel);
            setStep('review');
            toast.success(t('pages.lfp_found', { n: res.data.leads.length }));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };

    const updateLead = async (leadId, patch) => {
        try {
            const res = await api.put(`/agenti/lead-finder/lead/${leadId}`, patch);
            setLeads((ls) => ls.map((l) => l.id === leadId ? res.data : l));
            setEditing(null);
            toast.success(t('common.saved') || 'OK');
        } catch (e) {
            toast.error(t('common.generic_error'));
        }
    };

    const removeLead = (leadId) => {
        setSelected((s) => ({ ...s, [leadId]: false }));
        setLeads((ls) => ls.filter((l) => l.id !== leadId));
    };

    const toggleAll = () => {
        const allSelected = leads.every((l) => selected[l.id]);
        const sel = {};
        leads.forEach((l) => { sel[l.id] = !allSelected; });
        setSelected(sel);
    };

    const sendBulk = async () => {
        const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
        if (ids.length === 0) { toast.error(t('pages.lfp_no_selected')); return; }
        const minutes = Math.ceil(ids.length * 30 / 60);
        if (!window.confirm(t('pages.lfp_confirm_send', { n: ids.length, min: minutes }))) return;
        setStep('sending');
        try {
            const res = await api.post(`/agenti/lead-finder/batch/${batch.id}/send`, { lead_ids: ids, confirm: true });
            setSendResult(res.data);
            setStep('done');
            toast.success(t('pages.lfp_sent_ok', { n: res.data.sent }));
            onCompleted?.(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
            setStep('review');
        }
    };

    const nSelected = Object.values(selected).filter(Boolean).length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl sm:max-h-[90vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-4 sm:p-6 rounded-none sm:rounded-lg" data-testid="lead-finder-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <Target className="w-6 h-6 text-[hsl(var(--accent))]" />
                        {t('pages.lfp_title')}
                    </DialogTitle>
                </DialogHeader>

                {/* Status banner */}
                {status && (
                    <div className={`mt-2 rounded-lg border p-3 text-sm ${(status.brave_api_configured || status.google_places_configured) ? 'border-[hsl(145_55%_34%)]/40 bg-[hsl(145_45%_94%)]' : 'border-[hsl(0_72%_52%)]/40 bg-[hsl(0_80%_96%)]'}`}>
                        {(status.brave_api_configured || status.google_places_configured) ? (
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="font-semibold">✓ {t('pages.lfp_status_ok')}</span>
                                <span className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">
                                    {status.brave_api_configured && <Badge variant="outline" className="text-[10px] h-4 px-1">Brave</Badge>}
                                    {status.google_places_configured && <Badge variant="outline" className="text-[10px] h-4 px-1">Maps</Badge>}
                                </span>
                                <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.lfp_quota')}: {status.remaining_today}/{status.max_daily_per_user} {t('pages.lfp_today')}</span>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2">
                                <ShieldAlert className="w-4 h-4 mt-0.5 text-[hsl(0_72%_52%)] shrink-0" />
                                <div>
                                    <div className="font-semibold">{t('pages.lfp_no_brave_title')}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{t('pages.lfp_no_brave_help')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* GDPR notice */}
                {step === 'input' && (
                    <div className="mt-3 rounded-lg border border-[hsl(45_100%_52%)]/30 bg-[hsl(45_100%_94%)] p-3 text-xs">
                        <div className="font-semibold flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> {t('pages.lfp_gdpr_title')}</div>
                        <p className="mt-1 text-[hsl(var(--muted-foreground))]">{t('pages.lfp_gdpr_body')}</p>
                    </div>
                )}

                {/* STEP 1: INPUT */}
                {step === 'input' && (
                    <div className="space-y-3 mt-4">
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.lfp_desc')}</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>{t('pages.lfp_sector')} *</Label>
                                <Input value={settore} onChange={(e) => setSettore(e.target.value)} placeholder={t('pages.lfp_sector_ph')} className="mt-1 h-11" data-testid="lfp-settore" />
                            </div>
                            <div>
                                <Label>{t('pages.lfp_location')}</Label>
                                <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('pages.lfp_location_ph')} className="mt-1 h-11" data-testid="lfp-location" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-end">
                            <div>
                                <Label>{t('pages.lfp_n_max')}</Label>
                                <Select value={String(nMax)} onValueChange={(v) => setNMax(parseInt(v))}>
                                    <SelectTrigger className="mt-1 h-11" data-testid="lfp-nmax"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {[5, 10, 15, 20, 25].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <Button onClick={search} disabled={loading || !settore.trim() || !(status?.brave_api_configured || status?.google_places_configured)} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] h-12 font-bold gap-2" data-testid="lfp-search">
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.lfp_searching')}</> : <><Search className="w-4 h-4" /> {t('pages.lfp_search_btn')}</>}
                        </Button>
                    </div>
                )}

                {/* STEP 2: REVIEW */}
                {step === 'review' && (
                    <div className="space-y-3 mt-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-[hsl(var(--border))]">
                            <div>
                                <div className="font-semibold">{leads.length} {t('pages.lfp_leads_found')}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{batch.settore} {batch.location && `· ${batch.location}`}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={toggleAll} data-testid="lfp-toggle-all">{leads.every((l) => selected[l.id]) ? t('pages.lfp_unselect_all') : t('pages.lfp_select_all')}</Button>
                                <span className="text-xs text-[hsl(var(--muted-foreground))]">{nSelected} {t('pages.lfp_selected')}</span>
                            </div>
                        </div>

                        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                            {leads.map((lead) => (
                                <div key={lead.id} className="rounded-lg border border-[hsl(var(--border))] p-3" data-testid={`lfp-lead-${lead.id}`}>
                                    <div className="flex items-start gap-2">
                                        <input
                                            type="checkbox"
                                            checked={!!selected[lead.id]}
                                            onChange={(e) => setSelected((s) => ({ ...s, [lead.id]: e.target.checked }))}
                                            className="mt-1"
                                            data-testid={`lfp-check-${lead.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="min-w-0">
                                                    <div className="font-semibold truncate flex items-center gap-2">
                                                        {lead.company_name}
                                                        {lead.source === 'google_places' && <Badge variant="outline" className="text-[10px] uppercase tracking-wider h-4 px-1">Maps</Badge>}
                                                    </div>
                                                    {lead.email ? (
                                                        <div className="text-xs text-[hsl(var(--accent))] flex items-center gap-1 truncate">
                                                            <Mail className="w-3 h-3 shrink-0" /> {lead.email}
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-[hsl(var(--muted-foreground))] italic flex items-center gap-1">
                                                            <Mail className="w-3 h-3 shrink-0 opacity-50" /> {t('pages.lfp_no_email')}
                                                        </div>
                                                    )}
                                                    {lead.phone && (
                                                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1 truncate">
                                                            <span className="inline-block w-3 h-3 shrink-0">📞</span> {lead.phone}
                                                        </div>
                                                    )}
                                                    <a href={lead.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1 truncate hover:text-[hsl(var(--accent))]">
                                                        <ExternalLink className="w-3 h-3 shrink-0" /> {lead.domain}
                                                    </a>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button size="sm" variant="ghost" onClick={() => setEditing(editing === lead.id ? null : lead.id)} className="h-8" data-testid={`lfp-edit-${lead.id}`}>
                                                        <Edit3 className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => removeLead(lead.id)} className="h-8 text-[hsl(var(--destructive))]">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            {editing === lead.id ? (
                                                <div className="mt-3 space-y-2">
                                                    <Input
                                                        defaultValue={lead.subject}
                                                        onBlur={(e) => e.target.value !== lead.subject && updateLead(lead.id, { subject: e.target.value })}
                                                        className="h-10 font-medium"
                                                        placeholder={t('pages.lfp_subject_ph')}
                                                        data-testid={`lfp-subject-${lead.id}`}
                                                    />
                                                    <Textarea
                                                        rows={6}
                                                        defaultValue={lead.body_text}
                                                        onBlur={(e) => e.target.value !== lead.body_text && updateLead(lead.id, { body_text: e.target.value })}
                                                        className="text-sm font-mono"
                                                        data-testid={`lfp-body-${lead.id}`}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="mt-2">
                                                    <div className="text-sm font-medium truncate">{lead.subject}</div>
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-0.5">{lead.body_text}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end pt-3 border-t border-[hsl(var(--border))]">
                            <Button variant="outline" onClick={() => setStep('input')}>{t('common.back') || 'Indietro'}</Button>
                            <Button onClick={sendBulk} disabled={nSelected === 0} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="lfp-send-bulk">
                                <Send className="w-4 h-4" /> {t('pages.lfp_send_selected', { n: nSelected })}
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 3: SENDING */}
                {step === 'sending' && (
                    <div className="py-12 text-center">
                        <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[hsl(var(--accent))]" />
                        <div className="font-semibold">{t('pages.lfp_sending_title')}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('pages.lfp_sending_hint')}</div>
                    </div>
                )}

                {/* STEP 4: DONE */}
                {step === 'done' && sendResult && (
                    <div className="py-6 text-center space-y-3">
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(145_45%_92%)] text-[hsl(145_55%_22%)]">
                            <Send className="w-6 h-6" />
                        </div>
                        <div className="font-display text-2xl">{t('pages.lfp_done_title')}</div>
                        <div className="grid grid-cols-3 gap-2 max-w-md mx-auto text-sm">
                            <div className="rounded-lg bg-[hsl(145_45%_92%)] p-3">
                                <div className="font-display text-2xl">{sendResult.sent}</div>
                                <div className="text-xs">{t('pages.lfp_sent')}</div>
                            </div>
                            <div className="rounded-lg bg-[hsl(var(--muted))] p-3">
                                <div className="font-display text-2xl">{sendResult.skipped}</div>
                                <div className="text-xs">{t('pages.lfp_skipped')}</div>
                            </div>
                            <div className="rounded-lg bg-[hsl(0_80%_96%)] p-3">
                                <div className="font-display text-2xl">{sendResult.errors}</div>
                                <div className="text-xs">{t('pages.lfp_errors')}</div>
                            </div>
                        </div>
                        <Button onClick={() => onOpenChange(false)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold h-11 mt-3" data-testid="lfp-close">
                            {t('common.close') || 'Chiudi'}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


// =========================================================
// Preventivo Auto Dialog
// =========================================================
function PreventivoAutoDialog({ open, onOpenChange, onCompleted }) {
    const { t, i18n } = useTranslation();
    const [descrizione, setDescrizione] = useState('');
    const [cliente, setCliente] = useState('');
    const [save, setSave] = useState(true);
    const [loading, setLoading] = useState(false);

    // Voice recording state
    const [recState, setRecState] = useState('idle'); // 'idle' | 'recording' | 'transcribing'
    const [recSeconds, setRecSeconds] = useState(0);
    const mediaRecorderRef = React.useRef(null);
    const chunksRef = React.useRef([]);
    const streamRef = React.useRef(null);
    const timerRef = React.useRef(null);
    const stopTimeoutRef = React.useRef(null);

    const MAX_REC_SECONDS = 120; // 2 minutes

    const cleanupRecording = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (stopTimeoutRef.current) { clearTimeout(stopTimeoutRef.current); stopTimeoutRef.current = null; }
        if (streamRef.current) {
            try { streamRef.current.getTracks().forEach(tr => tr.stop()); } catch (_) { /* noop */ }
            streamRef.current = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];
    };

    useEffect(() => {
        if (open) { setDescrizione(''); setCliente(''); setSave(true); setRecSeconds(0); setRecState('idle'); }
        else { cleanupRecording(); }
        return () => { cleanupRecording(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const transcribeBlob = async (blob) => {
        if (!blob || blob.size < 1000) {
            setRecState('idle');
            toast.error(t('pages.ag_pa_voice_too_short'));
            return;
        }
        setRecState('transcribing');
        const ext = (blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm');
        const fd = new FormData();
        fd.append('audio', blob, `voice.${ext}`);
        fd.append('language', (i18n.language || 'it').slice(0, 2));
        try {
            const res = await api.post('/agenti/preventivo-auto/transcribe', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
            });
            const transcript = (res.data?.text || '').trim();
            if (!transcript) {
                toast.error(t('pages.ag_pa_voice_empty'));
            } else {
                setDescrizione(prev => prev ? (prev.trim() + '\n' + transcript) : transcript);
                toast.success(t('pages.ag_pa_voice_done'));
            }
        } catch (e) {
            const detail = e.response?.data?.detail || '';
            const status = e.response?.status;
            if (status === 503) {
                toast.error(t('pages.ag_pa_voice_not_configured'));
            } else if (status === 402) {
                // Quota / billing → show the friendly message returned by backend
                toast.error(detail || t('pages.ag_pa_voice_quota'));
            } else {
                toast.error(detail || t('pages.ag_pa_voice_error'));
            }
        } finally {
            setRecState('idle');
            setRecSeconds(0);
        }
    };

    const startRecording = async () => {
        if (recState !== 'idle') return;
        if (typeof navigator === 'undefined' || !navigator.mediaDevices || !window.MediaRecorder) {
            toast.error(t('pages.ag_pa_voice_unsupported'));
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Pick a mime supported by the browser
            const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
            const mimeType = candidates.find(m => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m)) || '';
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            mediaRecorderRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
            mr.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
                cleanupRecording();
                await transcribeBlob(blob);
            };
            mr.start();
            setRecState('recording');
            setRecSeconds(0);
            timerRef.current = setInterval(() => {
                setRecSeconds(s => {
                    const next = s + 1;
                    if (next >= MAX_REC_SECONDS) {
                        // auto-stop at limit
                        try { mr.state === 'recording' && mr.stop(); } catch (_) { /* noop */ }
                    }
                    return next;
                });
            }, 1000);
            // hard safety timeout (2 min + 1s buffer)
            stopTimeoutRef.current = setTimeout(() => {
                try { mr.state === 'recording' && mr.stop(); } catch (_) { /* noop */ }
            }, (MAX_REC_SECONDS + 1) * 1000);
        } catch (e) {
            cleanupRecording();
            setRecState('idle');
            toast.error(t('pages.ag_pa_voice_mic_denied'));
        }
    };

    const stopRecording = () => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state === 'recording') {
            try { mr.stop(); } catch (_) { /* noop */ }
        } else {
            cleanupRecording();
            setRecState('idle');
        }
    };

    const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(1, '0')}:${String(s % 60).padStart(2, '0')}`;

    const submit = async () => {
        if (descrizione.trim().length < 10) { toast.error(t('pages.ag_pa_short')); return; }
        setLoading(true);
        try {
            const res = await api.post('/agenti/preventivo-auto/generate', { descrizione, cliente_nome: cliente, save_to_db: save });
            toast.success(save ? t('pages.ag_pa_generated_saved') : t('pages.ag_pa_generated'));
            onCompleted(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };

    const isBusy = recState !== 'idle' || loading;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) cleanupRecording(); onOpenChange(o); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><DollarSign className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.ag_pa_title')}</DialogTitle></DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.ag_pa_desc')}</p>
                <div className="space-y-3 mt-3">
                    <div><Label>{t('pages.ag_pa_client')}</Label><Input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder={t('pages.ag_pa_client_ph')} data-testid="pa-cliente" /></div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label>{t('pages.ag_pa_desc_field')}</Label>
                            {recState === 'idle' && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={startRecording}
                                    disabled={loading}
                                    className="h-8 gap-1.5 text-xs"
                                    data-testid="pa-voice-start"
                                >
                                    <Mic className="w-3.5 h-3.5" />
                                    {t('pages.ag_pa_voice_record')}
                                </Button>
                            )}
                            {recState === 'recording' && (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={stopRecording}
                                    className="h-8 gap-1.5 text-xs animate-pulse"
                                    data-testid="pa-voice-stop"
                                >
                                    <Square className="w-3.5 h-3.5" />
                                    {t('pages.ag_pa_voice_stop')} · {fmtTime(recSeconds)} / {fmtTime(MAX_REC_SECONDS)}
                                </Button>
                            )}
                            {recState === 'transcribing' && (
                                <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]" data-testid="pa-voice-transcribing">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    {t('pages.ag_pa_voice_transcribing')}
                                </div>
                            )}
                        </div>
                        <Textarea rows={6} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder={t('pages.prev_form_descr_ph')} disabled={recState === 'transcribing'} data-testid="pa-descrizione" />
                        {recState === 'recording' && (
                            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{t('pages.ag_pa_voice_hint')}</p>
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="rounded" data-testid="pa-save" />
                        <span>{t('pages.ag_pa_save_chk')}</span>
                    </label>
                    <Button onClick={submit} disabled={isBusy || descrizione.length < 10} className="w-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] h-11 font-bold gap-2" data-testid="pa-submit">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.ag_pa_generating')}</> : <><Sparkles className="w-4 h-4" /> {t('pages.ag_pa_generate')}</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


// =========================================================
// Settings Panel (cron + auto-send + Resend custom domain)
// =========================================================
function SettingsPanel() {
    const { t } = useTranslation();
    const [s, setS] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newKey, setNewKey] = useState('');

    useEffect(() => {
        api.get('/agenti/settings').then(r => setS(r.data)).catch(() => toast.error(t('pages.ag_settings_load_err')));
    }, [t]);

    if (!s) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

    const save = async () => {
        setLoading(true);
        try {
            const payload = { ...s, resend_api_key: newKey || '' };
            ['_id', 'owner_id', 'resend_api_key_masked', 'configured', 'updated_at'].forEach(k => delete payload[k]);
            const res = await api.put('/agenti/settings', payload);
            setS(res.data);
            setNewKey('');
            toast.success(t('pages.ag_settings_saved'));
        } catch (e) {
            toast.error(t('pages.ag_settings_save_err'));
        } finally { setLoading(false); }
    };

    return (
        <div className="space-y-5 max-w-3xl">
            {/* Cron automation */}
            <Card className="p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-5 h-5 text-[hsl(var(--accent))]" />
                    <h3 className="font-display text-xl">{t('pages.ag_settings_cron_title')}</h3>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pages.ag_settings_cron_desc')}</p>

                <label className="flex items-center justify-between p-3 rounded-lg border cursor-pointer">
                    <div>
                        <div className="font-medium text-sm">{t('pages.ag_settings_cron_toggle')}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_settings_cron_toggle_sub')}</div>
                    </div>
                    <input type="checkbox" checked={s.cron_enabled} onChange={(e) => setS({ ...s, cron_enabled: e.target.checked })} className="w-5 h-5" data-testid="settings-cron-enabled" />
                </label>

                {s.cron_enabled && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('pages.ag_settings_cron_hour')}</Label>
                            <Select value={String(s.cron_hour)} onValueChange={(v) => setS({ ...s, cron_hour: parseInt(v) })}>
                                <SelectTrigger data-testid="settings-cron-hour"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 24 }, (_, i) => i).map(h => (
                                        <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00 ({s.cron_timezone || 'Europe/Rome'})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                <div className="mt-4 space-y-2">
                    <label className="flex items-center justify-between p-3 rounded-lg border cursor-pointer">
                        <div>
                            <div className="font-medium text-sm">{t('pages.ag_settings_autosend_reminders')}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_settings_autosend_reminders_sub')}</div>
                        </div>
                        <input type="checkbox" checked={s.auto_send_solleciti} onChange={(e) => setS({ ...s, auto_send_solleciti: e.target.checked })} className="w-5 h-5" data-testid="settings-autosend-solleciti" />
                    </label>
                    <label className="flex items-center justify-between p-3 rounded-lg border cursor-pointer">
                        <div>
                            <div className="font-medium text-sm">{t('pages.ag_settings_autosend_followup')}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.ag_settings_autosend_followup_sub')}</div>
                        </div>
                        <input type="checkbox" checked={s.auto_send_followup} onChange={(e) => setS({ ...s, auto_send_followup: e.target.checked })} className="w-5 h-5" data-testid="settings-autosend-followup" />
                    </label>
                </div>
            </Card>

            {/* Resend custom domain */}
            <Card className="p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-5 h-5 text-[hsl(var(--accent))]" />
                    <h3 className="font-display text-xl">{t('pages.ag_settings_resend_title')}</h3>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t('pages.ag_settings_resend_desc')}</p>

                <div className="space-y-3">
                    <div>
                        <Label>{t('pages.ag_settings_resend_key')}</Label>
                        <Input
                            type="password"
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            placeholder={s.resend_api_key_masked || 're_xxxxxxxxxxxxxxxxxx'}
                            data-testid="settings-resend-key"
                        />
                        {s.resend_api_key_masked && (
                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('pages.ag_settings_resend_current', { key: s.resend_api_key_masked })}</div>
                        )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('pages.ag_settings_from_email')}</Label>
                            <Input value={s.resend_from_email || ''} onChange={(e) => setS({ ...s, resend_from_email: e.target.value })} placeholder="info@tuaazienda.it" data-testid="settings-from-email" />
                        </div>
                        <div>
                            <Label>{t('pages.ag_settings_from_name')}</Label>
                            <Input value={s.resend_from_name || ''} onChange={(e) => setS({ ...s, resend_from_name: e.target.value })} placeholder="Mario Costruzioni Srl" data-testid="settings-from-name" />
                        </div>
                    </div>
                </div>
            </Card>

            <Button onClick={save} disabled={loading} className="w-full bg-[hsl(220_13%_10%)] text-white h-11 gap-2" data-testid="settings-save">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('pages.ag_settings_save')}
            </Button>
        </div>
    );
}
